import { isEmail, isPhoneNumber } from '@onefootprint/core';
import { PhoneInput, TextInput } from '@onefootprint/ui';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type UserSpecificFormData = {
  email: string;
  phone: string;
  external_id: string | undefined;
};

export type UserSpecificFormHandle = {
  setExternalIdError: (message: string) => void;
};

export type UserSpecificFormProps = {
  onSubmit: (data: UserSpecificFormData) => void;
  defaultValues?: UserSpecificFormData;
  requiresAuthentication?: boolean;
};

const UserSpecificForm = forwardRef<UserSpecificFormHandle, UserSpecificFormProps>(
  (
    { onSubmit, defaultValues = { email: '', phone: '', external_id: undefined }, requiresAuthentication = true },
    ref,
  ) => {
    const { t } = useTranslation('playbooks', {
      keyPrefix: 'table.actions.copyLinkDialog.config.attributes',
    });

    const form = useForm<UserSpecificFormData>({
      defaultValues,
      mode: 'onChange',
    });

    useImperativeHandle(
      ref,
      () => ({
        setExternalIdError: (message: string) => {
          form.setError('external_id', { type: 'server', message }, { shouldFocus: true });
        },
      }),
      [form],
    );

    const handleSubmit = form.handleSubmit(onSubmit);
    const [email, phone] = useWatch({ control: form.control, name: ['email', 'phone'] });

    return (
      <FormProvider {...form}>
        <form id="user-specific-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Controller
            control={form.control}
            name="phone"
            rules={{
              validate: value => {
                if (!value) {
                  return true;
                }
                return isPhoneNumber(value) || t($ => $.phone.errors.invalid);
              },
            }}
            render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
              <PhoneInput
                size="compact"
                name={name}
                label={t($ => $.phone.label)}
                onBlur={onBlur}
                value={value}
                popper
                onChange={onChange}
                hasError={!!error}
                hint={error?.message}
              />
            )}
          />
          <Controller
            control={form.control}
            name="email"
            rules={{
              validate: value => {
                if (!value) {
                  return true;
                }
                return isEmail(value) || t($ => $.email.errors.invalid);
              },
            }}
            render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
              <TextInput
                size="compact"
                name={name}
                onBlur={onBlur}
                value={value}
                onChange={onChange}
                label={t($ => $.email.label)}
                placeholder={t($ => $.email.placeholder)}
                type="email"
                hasError={!!error}
                hint={error?.message}
              />
            )}
          />
          <Controller
            control={form.control}
            name="external_id"
            rules={{
              validate: value => {
                if (!requiresAuthentication && (email || phone) && !value) {
                  return t($ => $.external_id.errors.required);
                }
                return true;
              },
            }}
            render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
              <TextInput
                size="compact"
                name={name}
                onBlur={onBlur}
                value={value || ''}
                onChange={e => {
                  if (error?.type === 'server') {
                    form.clearErrors('external_id');
                  }
                  onChange(e);
                }}
                label={t($ => $.external_id.label)}
                placeholder={t($ => $.external_id.placeholder)}
                hasError={!!error}
                hint={error?.message}
              />
            )}
          />
        </form>
      </FormProvider>
    );
  },
);

UserSpecificForm.displayName = 'UserSpecificForm';

export default UserSpecificForm;
